/**
 * Patient Status Model
 *
 * 5 canonical workflow statuses. Old DynamoDB values stay in place —
 * normalizeStatus() translates at read time.
 */

export const ALL_STATUSES = [
  {
    value: 'to_contact',
    label: 'To Contact',
    color: 'bg-amber-100 text-amber-700',
    icon: 'UserPlus',
  },
  {
    value: 'contacted',
    label: 'Contacted',
    color: 'bg-blue-100 text-blue-700',
    icon: 'MessageSquare',
  },
  {
    value: 'follow_up',
    label: 'Follow Up',
    color: 'bg-orange-100 text-orange-700',
    icon: 'RotateCcw',
  },
  {
    value: 'recovered',
    label: 'Recovered',
    color: 'bg-green-100 text-green-700',
    icon: 'HeartPulse',
  },
  {
    value: 'closed',
    label: 'Closed',
    color: 'bg-gray-200 text-gray-700',
    icon: 'XCircle',
  },
];

// Identical — no more systemOnly filtering needed
export const UI_STATUSES = ALL_STATUSES;

/**
 * Maps every legacy DynamoDB value to one of the 5 canonical statuses.
 */
export const LEGACY_STATUS_MAP = {
  // → to_contact
  needs_review: 'to_contact',
  unknown: 'to_contact',
  active: 'to_contact',
  urgent: 'to_contact',           // "urgent" as a status → to_contact (risk is separate)
  // → contacted
  contacted: 'contacted',
  message_sent_sms: 'contacted',
  message_sent_email: 'contacted',
  contacted_manual: 'contacted',
  // → follow_up
  needs_follow_up: 'follow_up',
  responded: 'follow_up',         // replied but not recovered
  // → recovered
  recovered: 'recovered',
  // → closed
  closed: 'closed',
  declined: 'closed',
  unreachable: 'closed',
};

/**
 * Reasons a case can be closed. Used in the drawer dropdown.
 */
export const CLOSED_REASONS = [
  { value: 'declined_care', label: 'Declined care' },
  { value: 'unreachable', label: 'Unreachable' },
  { value: 'wrong_contact', label: 'Wrong contact info' },
  { value: 'no_longer_patient', label: 'No longer a patient' },
  { value: 'other', label: 'Other' },
];

/**
 * Translate any raw DB value (including null) to one of the 5 canonical statuses.
 */
export function normalizeStatus(raw) {
  if (!raw) return 'to_contact';
  const key = raw.toLowerCase().trim();
  return LEGACY_STATUS_MAP[key] || 'to_contact';
}

/**
 * Returns the full status object { value, label, color, icon } for any raw DB value.
 */
export function getStatusObject(raw) {
  const canonical = normalizeStatus(raw);
  return ALL_STATUSES.find(s => s.value === canonical) || ALL_STATUSES[0];
}
